import { Component, NgZone, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Observable, of, throwError } from 'rxjs';
import { takeUntil, finalize, map, switchMap, filter, tap, catchError } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { numbersOnlyValidator, whiteSpacesValidator } from '../shared/validatorHelper';
import { AppInsightsService } from '../shared/services/appinsights/appinsights.service';
import { Customer, Restrictions, RestrictionTypes } from '../shared/services/customer/Customer';
import { CustomerService } from '../shared/services/customer/customer.service';
import { Feature } from '../shared/services/Features/Feature';
import { FeaturesService } from '../shared/services/Features/features.service';
import { Locale } from '../shared/templates/Locale/Locale';
import { ConfigService, LocalesService } from '../shared/templates/Locale/locales.service';
import { SpinnerService } from '../shared/templates/spinner/spinner.service';
import { SampleBillModal } from './sampleBillModal.component';
import { LanguageService } from '../shared/services/language/language.service';
import { PromptComponent } from '../shared/templates/prompt/prompt.component';
import { StateService } from '../shared/services/state/state.service';

@Component({
  selector: 'app-customer',
  templateUrl: './customer.component.html'
})
export class CustomerComponent implements OnInit, OnDestroy {
  customer = {} as Customer;
  feature = {} as Feature;
  retryCount = 0;
  maxRetry: number;
  isReRouteOnRetryEnabled: boolean;
  validation_errors: Array<string> = [];
  action: string;
  lang: string;
  view: string = "OPT_ALL";
  isAltFormRequired: boolean;
  IsPhaseRequired:boolean;
  utilityprovider: string;
  tooltipPosition = "below";
  observableLocales$: Observable<Locale[]>;
  accountId: string //retains the account number that the user typed. Can be used when form is reset
  inputAccountLength = 10;
  inputZipLength = 5;
  isSubmitting: boolean = false;
  customerForm: UntypedFormGroup;
  private ngUnsubscribe$: Subject<boolean> = new Subject();
  isCCARestrictedAccessEnable: boolean = false;
  isCustomerRestrictedAccess: boolean = false;
  isCustomerPromptEnable:boolean = false;
  isCrmEndpointRestrictionEnabled: boolean = false;

  RestrictionsType:Restrictions[];

  private readonly formFieldOrder = ['accountNumber', 'name', 'zipcode'];
  constructor(private customerService: CustomerService, private featureService: FeaturesService, private localeService: LocalesService,
    private route: ActivatedRoute, private router: Router, private spinnerService: SpinnerService, public dialog: MatDialog,
    private appInsightsService: AppInsightsService, private configService: ConfigService, private languageService: LanguageService, private stateService: StateService,
    private _ngZone: NgZone) { }

  //Initializes the component. Sets up the form, calls all the services
  ngOnInit(): void {
    this.appInsightsService.logPageView('CustomerComponent');
    this.isSubmitting = true;

    this.route.params
      .pipe(takeUntil(this.ngUnsubscribe$))
      .subscribe(params => {
        this.action = params['action'];
        this.lang = params['lang'];
        this.stateService.setLanguage(params['lang']);
      })
    this.languageService.textDirection(this.lang);
    this.getFeature();
    this.configService.languagerequested = this.lang;
    this.configService.iframeAction = this.action;
    this.observableLocales$ = this.getPlaceholderLocaleStrings();
    this.setReTryOptions();
    this.customerForm = new UntypedFormGroup({
      'accountNumber': new UntypedFormControl(this.customer.accountId, [
        Validators.required,
        Validators.minLength(this.inputAccountLength),
        numbersOnlyValidator()
      ]),
      'name': new UntypedFormControl(this.customer.name, [
        Validators.required,
        whiteSpacesValidator()
      ]),
      'zipcode': new UntypedFormControl(this.customer.zipcode, [
        Validators.required,
        Validators.minLength(this.inputZipLength),
        numbersOnlyValidator()
      ]),
      'isNavigatingToAltForm': new UntypedFormControl(false)
    }, { updateOn: 'blur' });


    switch (this.action) {
      case 'optout':
        this.view = 'OPT_OUT'
        break;
      case 'optdwn':
        this.view = 'OPT_DWN';
        break;
      case 'optup':
        this.view = 'OPT_UP';
        break;
      case 'optin':
        this.view = 'OPT_IN'
        break;
      default:
        this.view = 'OPT_ALL';
        break;
    }

    this.isSubmitting = false;
  }

  openDialog(): void {
    let dialogRef = this.dialog.open(SampleBillModal, {
      data: { lang: this.lang, provider: this.utilityprovider },
      panelClass: 'sample-bill-dialog-container'
    });
  }

  ///Access methods for formcontrols
  get name() { return this.customerForm.get('name'); }
  get accountNumber() { return this.customerForm.get('accountNumber'); }
  get zipcode() { return this.customerForm.get('zipcode'); }

  ///Fetches data needed for the retry option
  setReTryOptions(): void {
    if (this.maxRetry == null || this.maxRetry == undefined) {
      this.maxRetry = 3;
    }
  }

  // returns boolean observable of true if the Instruction Locale is available
  hasInstructionsLocale() {
    return this.localeService.getLocalesByLang(this.lang).pipe(
      map((locales: Locale[]) => {
        let hasLocale: boolean = false;
        locales.forEach(locale => {
          if (locale.key === 'INSTRUCTIONS' && (locale.view == null || locale.view == undefined || locale.view === this.view)) {
            if (!(locale.value === null || locale.value === undefined || locale.value.length < 1)) {
              hasLocale = true;
            }
          }
        });
        return hasLocale;
      })
    );
  }

  navigateToAltForm(): void {
    if (this.accountId !== null && this.accountId !== undefined) {
      this.router.navigate(['/alt', this.action, this.lang, this.accountId]);
    } else {
      this.router.navigate(['/alt',this.action, this.lang]);
    }
  }

  ///Validates the form. If valid, calls the service to fetch account.
  ///Error processing based on service call result. If successfull, navigate to the next page
  OnSubmit(): void {
    this.stateService.setAccountNumber(this.accountNumber.value);
    this.accountId = this.stateService.getAccountNumber()
    this.appInsightsService.logEvent('User submitted account information on Verification page.');
    if (this.customerForm.controls['isNavigatingToAltForm'].value === true) {
      this.navigateToAltForm();
    }
    this.isSubmitting = true;
    this.validation_errors = [];
    this.markFormGroupTouched(this.customerForm);
    if (this.customerForm.invalid) {
      this.isSubmitting = false;
      this.scrollToError();
      return;
    }
   //only if is true, do second call
    this.customerService.getCustomer(this.accountNumber.value ,this.name.value, this.zipcode.value, this.action)
      .pipe(
        tap(c => this.customer = c),
        switchMap(d =>
            this.isCCARestrictedAccessEnable && this.customer?.ldcAccounts?.length > 0 ?
                this.customerService.isCustomerRestricted(d.ldcAccounts.map(c => c.contactId), this.action)
        .pipe(tap(r => {
          this.isCustomerRestrictedAccess = r.find(r => r.restrictionType == RestrictionTypes.restriction)?.isRestricted ?? false;
          this.isCustomerPromptEnable = r.find(r => r.restrictionType == RestrictionTypes.prompt)?.isRestricted ?? false;
          })) :
           of(false)
        ),
        catchError((err) => {
          this.validation_errors.push(err.statusText.replace(" ", "").toUpperCase());
          this.focusErrorSummary();
          return throwError(err);
        }))
      .subscribe(
        () => {
            this.retryCount++;
            if (this.retryCount >= this.maxRetry) {
              if (this.isReRouteOnRetryEnabled) {
                //No more Tries
                //Navigate to AltPage
                this.navigateToAltForm();
              }
            }

            if (this.customer !== null && this.customer !== undefined) {
              if (!this.customer.isVerified) {
                if (this.action.toLowerCase() == 'optout') {
                  this.validation_errors.push("OPTOUT_ERR");
                } else {
                  if (this.IsPhaseRequired && (this.customer.phase === null || this.customer.phase === undefined)) {
                    //does not have a phase
                    this.validation_errors.push("PHASE");
                  } else if (this.customer.effectiveDate !== null && this.customer.effectiveDate !== undefined) {

                    if (new Date(this.customer.effectiveDate).getTime() === Date.parse('0001-01-01T00:00:00')) {
                      //Not valid till effective date
                      this.validation_errors.push("DATE_NO_EFFECTIVE");
                    } else {
                      //Valid till effective date
                      this.validation_errors.push("DATE");
                    }
                  } else {
                    this.validation_errors.push("UNKNOWN");
                  }
                }
                this.isSubmitting = false;
                this.focusErrorSummary();
                return;
              }
              else {
                //set user
                this.stateService.setCustomer(this.customer);
                if (this.action.toLowerCase() == 'optout') {
                  if (this.customer.isOptOutRestricted) {
                    this.validation_errors.push("OPT_OUT_RESTRICTED");
                    this.focusErrorSummary();
                  }
                  else if (this.isCustomerRestrictedAccess) { //cca restricted
                      this.validation_errors.push("OPT_OUT_CCARESTRICTED");
                      this.focusErrorSummary();
                  }
                  else {
                    //navigate to OptOutComponent
                    //before navigation display prompt message
                    if(this.isCCARestrictedAccessEnable && this.isCustomerPromptEnable && this.feature.optOutConfig.isPromptEnabled){
                      this.openRetentionDialog("optout");
                   }
                   else{
                    this.navigateToAction("optout");
                   }
                  }
                } else if (this.action.toLocaleLowerCase() == 'optup' && !this.customer.isProgramOptionsAvailable) {
                  this.validation_errors.push("NO_PROGRAM_OPTIONS");
                  this.focusErrorSummary();
                } else if (this.customer.isRestricted) {
                  this.validation_errors.push("OPT_RESTRICTED");
                  this.focusErrorSummary();
                } else if (this.isCustomerRestrictedAccess) { //cca restricted
                  this.validation_errors.push("OPT_CCARESTRICTED");
                  this.focusErrorSummary();
                }
                else {
                  //navigate to OptComponent
                  //before navigation display prompt message
                  if(this.isCCARestrictedAccessEnable && this.isCustomerPromptEnable && this.feature.optInConfig.isPromptEnabled){
                     this.openRetentionDialog("opt");
                  }
                  else{
                    this.navigateToAction("opt");
                  }
                }
              }
            } else {
              this.validation_errors.push("NOCONTENT");
              this.focusErrorSummary();
            }
            this.isSubmitting = false;
        });
    this.customerForm.reset();
  }

  navigateToAction(action: string): void {
    if (action == "opt") {
      this.router.navigate(['/opt', this.action, this.lang, this.customer.accountId, this.customer.name, this.customer.zipcode]);
    }
    else if (action == "optout") {
      this.router.navigate(['/optout', this.lang, this.customer.accountId, this.customer.name, this.customer.zipcode]);
    }
  }

    getFeature() {
        this.featureService.getFeature()
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe(
                (f: Feature) => {
                    this.maxRetry = f.maxReTries;
                    this.isReRouteOnRetryEnabled = f.isReRouteAfterReTryEnabled;
                    this.isAltFormRequired = f.isAltFormEnabled;
                    this.utilityprovider = f.provider;
                    this.IsPhaseRequired = f.isPhaseRequired;
                    this.isCCARestrictedAccessEnable = f.isRestrictedAccessEnable;
                    this.isCrmEndpointRestrictionEnabled = f.isCRMEndpointReenrollmentEnabled;
                    this.feature = f;

                    if (f.provider == 'SDGE' || f.provider == 'SCE') {
                        this.inputAccountLength = 12;
                        this.accountNumber.setValidators([Validators.minLength(this.inputAccountLength)])
                    }
                },  //map data
                err => { //handle error
                    //console.log(err);
                    this.validation_errors.push(err.statusText.replace(" ", "").toUpperCase());
                });
    }

  getPlaceholderLocaleStrings() {
    return this.localeService.getLocalesByLang(this.lang)
      .pipe(map((locales: Locale[]) => {
        return locales.filter((item) => {
          return item.key.endsWith('TOOLTIP') && (item.view == null || item.view == undefined || item.view === 'CUSTOMER');
        });
      }));
  }

  markFormGroupTouched(formGroup: UntypedFormGroup) {
    (<any>Object).values(formGroup.controls).forEach(control => {
      control.markAsTouched();

      if (control.controls) {
        this.markFormGroupTouched(control);
      }
    });
  }

  scrollToError(): void {
    this._ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        const firstInvalidId = this.formFieldOrder.find(id => this.customerForm.get(id)?.invalid);
        if (firstInvalidId) {
          (document.getElementById(firstInvalidId) as HTMLElement)?.focus();
        }
      });
    });
  }

  focusErrorSummary(): void {
    this._ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        (document.getElementById('customer-error-summary') as HTMLElement)?.focus();
      });
    });
  }

  ///Method called OnDestroy to unsubscribe from all subscriptions
  ngOnDestroy() {
    this.ngUnsubscribe$.next();
    this.ngUnsubscribe$.complete();
  }

  openRetentionDialog(action:string): void {
    let dialogRef = this.dialog.open(PromptComponent, {
      closeOnNavigation: false,
      disableClose: true,
      data: { localeView: this.view },
      maxWidth: '100vw',
      maxHeight: '100vh',
      height: '100%',
      width: '100%'
    }).afterClosed().subscribe(data => {
      if(data) {
        this.navigateToAction(action);
      }

    });
  }

}
